import { useEffect } from 'react'

function setMeta(name: string, content: string, property?: boolean) {
  const attr = property ? 'property' : 'name'
  let el = document.querySelector(
    `meta[${attr}="${name}"]`,
  ) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, name)
    document.head.appendChild(el)
  }
  el.content = content
}

function addLink(rel: string, href: string, extra?: Record<string, string>) {
  if (document.querySelector(`link[rel="${rel}"][href="${href}"]`)) return
  const link = document.createElement('link')
  link.rel = rel
  link.href = href
  if (extra) Object.entries(extra).forEach(([k, v]) => link.setAttribute(k, v))
  document.head.appendChild(link)
}

export function useSEO() {
  useEffect(() => {
    // Title
    document.title =
      'Reflujo Cero en 72h — Protocolo Natural para Reducir el Reflujo Ácido en 3 Días'

    // Core meta
    setMeta(
      'description',
      'Protocolo natural de 72 horas para reducir el ardor, la subida de ácido y la presión en el pecho. Sin omeprazol. Guía digital con garantía de 7 días.',
    )
    setMeta(
      'keywords',
      'reflujo ácido, agrura nocturna, hernia hiatal, protocolo natural reflujo, eliminar reflujo, remedios naturales reflujo, acidez estomacal, reflujo gastroesofágico, sábila para reflujo, Reflujo Cero en 72h',
    )
    setMeta('author', 'Hugo Pagano')
    setMeta('robots', 'index, follow')

    // Viewport (ensure it exists)
    setMeta('viewport', 'width=device-width, initial-scale=1')

    // Language
    document.documentElement.lang = 'es'

    // Open Graph
    setMeta(
      'og:title',
      'Reflujo Cero en 72h — Reduce el Ardor y Vuelve a Dormir',
      true,
    )
    setMeta(
      'og:description',
      'Protocolo natural de 72 horas para reducir el ardor, la subida de ácido y la presión en el pecho. Sin omeprazol. Solo 12 USD.',
      true,
    )
    setMeta('og:type', 'website', true)
    setMeta('og:locale', 'es_ES', true)
    setMeta('og:site_name', 'Reflujo Cero en 72h', true)
    setMeta(
      'og:image',
      'https://i.postimg.cc/9MBfVQ1x/libro-principal.webp',
      true,
    )

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image')
    setMeta(
      'twitter:title',
      'Reflujo Cero en 72h — Reduce el Ardor y Vuelve a Dormir',
    )
    setMeta(
      'twitter:description',
      'Protocolo natural de 72 horas para reducir el ardor, la subida de ácido y la presión en el pecho. Sin omeprazol. Garantía de 7 días.',
    )
    setMeta(
      'twitter:image',
      'https://i.postimg.cc/9MBfVQ1x/libro-principal.webp',
    )

    // Preconnect to external domains for faster loading
    addLink('preconnect', 'https://fonts.googleapis.com')
    addLink('preconnect', 'https://fonts.gstatic.com', { crossorigin: '' })
    addLink('preconnect', 'https://i.postimg.cc')
    addLink('preconnect', 'https://images.unsplash.com')
    addLink('dns-prefetch', 'https://connect.facebook.net')
    addLink('dns-prefetch', 'https://pay.hotmart.com')

    // Preload critical font files for faster text rendering
    addLink(
      'preload',
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Playfair+Display:wght@700;800;900&display=swap',
      { as: 'style' },
    )

    // Canonical URL
    addLink('canonical', window.location.origin + window.location.pathname)

    // Structured Data — Product
    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Reflujo Cero en 72h',
      description:
        'Protocolo natural de 72 horas para reducir el ardor, la subida de ácido y la presión en el pecho sin medicamentos.',
      image: 'https://i.postimg.cc/9MBfVQ1x/libro-principal.webp',
      brand: { '@type': 'Brand', name: 'Reflujo Cero en 72h' },
      author: { '@type': 'Person', name: 'Hugo Pagano' },
      offers: {
        '@type': 'Offer',
        price: '12',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: 'https://pay.hotmart.com/S104868471X',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '850',
      },
    }

    // Structured Data — FAQ
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '¿Tengo que dejar el café para siempre?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No, solo durante la fase de sanación de 72 horas para permitir que tu esófago cicatrice.',
          },
        },
        {
          '@type': 'Question',
          name: '¿Funciona si tengo hernia hiatal?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sí. El protocolo incluye técnicas específicas de desinflamación y posicionamiento para hernia hiatal.',
          },
        },
        {
          '@type': 'Question',
          name: '¿Cuánto tardaré en notar resultados?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'El alivio del ardor se nota en las primeras horas. La sanación del tejido toma el ciclo completo de 72 horas.',
          },
        },
        {
          '@type': 'Question',
          name: '¿Cómo accedo al contenido después de comprar?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'El acceso es inmediato y digital. Después de completar tu pago recibirás un correo con el enlace directo al material. En menos de 5 minutos puedes estar leyendo el protocolo desde tu celular, tablet o computadora.',
          },
        },
      ],
    }

    // Inject Product schema
    let productScript = document.querySelector(
      'script[data-seo-ld="product"]',
    ) as HTMLScriptElement | null
    if (!productScript) {
      productScript = document.createElement('script')
      productScript.type = 'application/ld+json'
      productScript.setAttribute('data-seo-ld', 'product')
      document.head.appendChild(productScript)
    }
    productScript.textContent = JSON.stringify(productSchema)

    // Inject FAQ schema
    let faqScript = document.querySelector(
      'script[data-seo-ld="faq"]',
    ) as HTMLScriptElement | null
    if (!faqScript) {
      faqScript = document.createElement('script')
      faqScript.type = 'application/ld+json'
      faqScript.setAttribute('data-seo-ld', 'faq')
      document.head.appendChild(faqScript)
    }
    faqScript.textContent = JSON.stringify(faqSchema)
  }, [])
}
